import React, { useState, useEffect, useCallback } from 'react';
import { venuesApi } from '../../api/venues';
import type { VenueStatusResponse, VenueInfo } from '../../types/venues';
import { useToast } from '../../hooks/useToast';

interface VenueControlProps {
  /** Refresh interval in milliseconds */
  refreshInterval?: number;
}

export function VenueControl({ refreshInterval = 5000 }: VenueControlProps) {
  const [status, setStatus] = useState<VenueStatusResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [switching, setSwitching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmVenue, setConfirmVenue] = useState<string | null>(null);
  const { showToast } = useToast();

  const fetchStatus = useCallback(async () => {
    try {
      const data = await venuesApi.getStatus();
      setStatus(data);
      setError(null);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch venue status';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, refreshInterval);
    return () => clearInterval(interval);
  }, [fetchStatus, refreshInterval]);

  const handleVenueSwitch = async (venue: string) => {
    setSwitching(true);
    try {
      const result = await venuesApi.setActive(venue);
      if (result.success) {
        showToast(`Venue switched from ${result.previous_venue} to ${result.new_venue}`, 'success');
        await fetchStatus();
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to switch venue';
      showToast(message, 'error');
    } finally {
      setSwitching(false);
      setConfirmVenue(null);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg p-6 border border-gray-200">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="h-10 bg-gray-200 rounded mb-2"></div>
          <div className="h-10 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg p-6 border border-red-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Order Dispatch</h3>
        <p className="text-red-600 text-sm">{error}</p>
        {error.includes('separated deployment') && (
          <p className="text-gray-500 text-xs mt-2">
            Venue control requires the API and OMS to run in the same process.
          </p>
        )}
      </div>
    );
  }

  if (!status) {
    return null;
  }

  return (
    <div className="bg-white rounded-lg p-6 border border-gray-200">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Order Dispatch</h3>

      {/* Active Venue Display */}
      <div className="mb-4">
        <p className="text-sm text-gray-600 mb-1">Active Venue</p>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-green-500"></div>
          <span className="font-medium text-gray-900">
            {status.available_venues.find((v: VenueInfo) => v.is_active)?.display_name || status.active_venue}
          </span>
        </div>
      </div>

      {/* Venue Selection */}
      <div className="space-y-2">
        <p className="text-sm text-gray-600">Switch Venue</p>
        <div className="flex flex-wrap gap-2">
          {status.available_venues.map((venue: VenueInfo) => (
            <button
              key={venue.name}
              onClick={() => {
                if (!venue.is_active) {
                  setConfirmVenue(venue.name);
                }
              }}
              disabled={venue.is_active || switching}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                venue.is_active
                  ? 'bg-green-100 text-green-800 cursor-default'
                  : switching
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {venue.display_name}
              {venue.is_active && ' (Active)'}
            </button>
          ))}
        </div>
      </div>

      {/* Confirmation Modal */}
      {confirmVenue && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
            <h4 className="text-lg font-semibold text-gray-900 mb-2">
              Confirm Venue Switch
            </h4>
            <p className="text-gray-600 mb-4">
              Are you sure you want to switch from{' '}
              <strong>{status.active_venue}</strong> to{' '}
              <strong>{confirmVenue}</strong>?
            </p>
            <p className="text-sm text-yellow-700 bg-yellow-50 p-3 rounded-md mb-4">
              New orders will be routed to the new venue. In-flight orders will
              continue with their original venue.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setConfirmVenue(null)}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md text-sm font-medium"
                disabled={switching}
              >
                Cancel
              </button>
              <button
                onClick={() => handleVenueSwitch(confirmVenue)}
                className="px-4 py-2 text-white bg-primary-600 hover:bg-primary-700 rounded-md text-sm font-medium disabled:opacity-50"
                disabled={switching}
              >
                {switching ? 'Switching...' : 'Confirm Switch'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Info text */}
      <p className="text-xs text-gray-500 mt-4">
        Management permission required to switch venues.
      </p>
    </div>
  );
}
